import React, { useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';

import CrmAnalysisLayout from '../../components/crmAnalysis/CrmAnalysisLayout';
import CsvUploadCard     from '../../components/crmAnalysis/CsvUploadCard';
import { useCrmAnalysis } from '../../context/CrmAnalysisContext';
import { apiService }     from '../../service/Api';

const UPLOAD_CONFIGS = [
  {
    key: 'deals',
    title: 'Deals',
    description: 'Required columns: Deal ID, Deal Name, Stage, Amount, Close Date, Company Name, Owner, …',
    upload: (file, tenantId) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenant_id', tenantId);
      return apiService.uploadCrmDealsCsv(fd, tenantId).then(r => r.data);
    },
  },
  {
    key: 'calls',
    title: 'Calls',
    description: 'Required columns: Call ID, Associated Deal, Company Name, Call Date, Duration, Outcome, Notes, …',
    upload: (file, tenantId) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenant_id', tenantId);
      return apiService.uploadCrmCallsCsv(fd, tenantId).then(r => r.data);
    },
  },
  {
    key: 'emails',
    title: 'Emails',
    description: 'Required columns: Email ID, Associated Deal, Company Name, Subject Line, Sent Date, Open Status, …',
    upload: (file, tenantId) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenant_id', tenantId);
      return apiService.uploadCrmEmailsCsv(fd, tenantId).then(r => r.data);
    },
  },
];

export default function CrmDataSync() {
  const { tenantname }             = useParams();
  const { state }                  = useLocation();
  const navigate                   = useNavigate();
  const { selectedTenant, setSelectedTenant } = useCrmAnalysis();

  // Restore tenant from navigation state if context was lost (e.g. page refresh)
  useEffect(() => {
    if (state?.tenantId && !selectedTenant) {
      setSelectedTenant({
        id:     state.tenantId,
        name:   state.tenantName || tenantname,
        domain: tenantname,
      });
    }
  }, [state, selectedTenant, setSelectedTenant, tenantname]);

  const tenantId = selectedTenant?.id ?? state?.tenantId;

  const handleGoToAnalysis = () => {
    navigate(`/crmanalysis/${tenantname}/analysis`, {
      state: { tenantId, tenantName: tenantname },
    });
  };

  return (
    <CrmAnalysisLayout tenantName={tenantname} tenantId={tenantId}>
      <div className="h-full overflow-y-auto px-6 py-6">

        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold" style={{ color: '#0A0A10' }}>Data Sync</h1>
          <p className="text-sm mt-1" style={{ color: '#0A0A10', opacity: 0.55 }}>
            Upload CRM exports (CSV) to sync deals, calls, and emails for&nbsp;
            <span style={{ fontWeight: 600, color: '#8B6914' }}>{tenantname}</span>.
            {!tenantId && (
              <span className="ml-2 text-xs font-medium" style={{ color: '#dc2626' }}>
                ⚠ Tenant ID not resolved — navigate here from the admin dashboard.
              </span>
            )}
          </p>
        </div>

        {/* Upload cards */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {UPLOAD_CONFIGS.map(({ key, title, description, upload }) => (
            <CsvUploadCard
              key={key}
              title={title}
              description={description}
              onUpload={(file) => {
                if (!tenantId) {
                  toast.error('Tenant ID is missing. Please navigate from the admin dashboard.');
                  throw new Error('No tenant ID');
                }
                return upload(file, tenantId);
              }}
            />
          ))}
        </div>

        {/* Run Analysis CTA */}
        <div
          className="mt-8 rounded-xl border p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}
        >
          <div>
            <p className="font-semibold" style={{ color: '#0A0A10' }}>Ready to analyse?</p>
            <p className="text-sm mt-0.5" style={{ color: '#0A0A10', opacity: 0.55 }}>
              Once your CSVs are uploaded, run an AI-powered deal analysis.
            </p>
          </div>
          <button
            onClick={handleGoToAnalysis}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all hover:opacity-90 whitespace-nowrap"
            style={{ backgroundColor: '#D7B472', color: '#FFFFFF' }}
          >
            <BarChart2 className="w-4 h-4" />
            Go to Analysis
          </button>
        </div>
      </div>
    </CrmAnalysisLayout>
  );
}
